import dayjs from 'dayjs'

import { CallDirection, CallStatus } from 'src/graphql/types/activityLogs'
import { ApplicantCallConversionStatus } from 'src/graphql/types/callActivity'
import { CallConnectedStatus } from 'src/graphql/types/callActivity'
import { LinkType } from 'src/graphql/types/trackingLinks'
import { LinkTypeDisplay } from 'src/graphql/types/trackingLinks'
import { VoiceAgentAnalysisResultName } from 'src/graphql/types/voiceAgentResult'
import { formatSnakeValue } from 'src/lib/formatters'
import { parseDateRangeFromUrl } from 'src/lib/url-date-range.utils'
import {
  getCommaSeparatedSearchParam,
  getSearchParam,
  setSearchParam,
} from 'src/lib/url-search-params.utils'
import { getNegativeVoEOutcomeLabel } from 'src/lib/voe/callOutcomeTag'

export { serializeDateRangeForUrl } from 'src/lib/url-date-range.utils'

export const callStatusColors = {
  [ApplicantCallConversionStatus.CONNECTED]: {
    [CallConnectedStatus.BOOKED]: {
      backgroundColor: 'bg-doubleNickel-success-100',
      textColor: 'text-doubleNickel-success-700',
      color: '#ABEFC6',
    },
    [CallConnectedStatus.TRANSFERRED]: {
      backgroundColor: 'bg-doubleNickel-success-100',
      textColor: 'text-doubleNickel-success-700',
      color: '#ABEFC6',
    },
    [CallConnectedStatus.OTHER]: {
      backgroundColor: 'bg-doubleNickel-brand-25',
      textColor: 'text-doubleNickel-brand-700',
      color: '#BBDFF3',
    },
    [CallConnectedStatus.NOT_AVAILABLE]: {
      backgroundColor: 'bg-doubleNickel-brand-25',
      textColor: 'text-doubleNickel-brand-700',
      color: '#BBDFF3',
    },
    [CallConnectedStatus.CALLBACK]: {
      backgroundColor: 'bg-doubleNickel-brand-25',
      textColor: 'text-doubleNickel-brand-700',
      color: '#BBDFF3',
    },
    [CallConnectedStatus.NOT_INTERESTED]: {
      backgroundColor: 'bg-doubleNickel-warning-100',
      textColor: 'text-doubleNickel-warning-600',
      color: '#FEF0C7',
    },
    [CallConnectedStatus.DISQUALIFIED]: {
      backgroundColor: 'bg-doubleNickel-error-100',
      textColor: 'text-doubleNickel-error-500',
      color: '#FECDCA',
    },
    [CallConnectedStatus.VERIFIED]: {
      backgroundColor: 'bg-doubleNickel-success-100',
      textColor: 'text-doubleNickel-success-700',
      color: '#ABEFC6',
    },
    [CallConnectedStatus.INFO_COLLECTED]: {
      backgroundColor: 'bg-doubleNickel-brand-25',
      textColor: 'text-doubleNickel-brand-700',
      color: '#BBDFF3',
    },
    // A mailbox, not a conversation. Same amber as the Voicemail chip the
    // Calls table draws for a recruiting call (StyledBadge "orange"), so a
    // VoE mailbox and a recruiting mailbox read identically.
    [CallConnectedStatus.VOICEMAIL]: {
      backgroundColor: 'bg-doubleNickel-warning-50',
      textColor: 'text-doubleNickel-warning-500',
      color: '#FEF0C7',
    },
    null: {
      backgroundColor: 'bg-doubleNickel-brand-25',
      textColor: 'text-doubleNickel-brand-700',
      color: '#BBDFF3',
    },
  },
  [ApplicantCallConversionStatus.NOT_CONNECTED]: {
    backgroundColor: 'bg-doubleNickel-gray-100',
    textColor: 'text-doubleNickel-gray-500',
    color: '#EAECF0',
  },
}

export const getStatusLabel = (
  status: string,
  connectedStatus: string,
  voeFindings?: readonly string[] | null
) => {
  const negativeVoEOutcome = getNegativeVoEOutcomeLabel(
    connectedStatus,
    voeFindings
  )
  if (negativeVoEOutcome) {
    return `${formatSnakeValue(status)} : ${negativeVoEOutcome}`
  }
  // A mailbox is not a connected conversation. The calls list and call
  // detail pass CONNECTED as the conversion status whenever a disposition
  // exists, which would otherwise render "Connected : Voicemail".
  if (connectedStatus === CallConnectedStatus.VOICEMAIL) {
    return 'Voicemail'
  }
  if (status == ApplicantCallConversionStatus.NOT_CONNECTED) {
    return formatSnakeValue(status)
  } else {
    return `${formatSnakeValue(status)} ${
      connectedStatus ? `: ${formatSnakeValue(connectedStatus)}` : ''
    }`
  }
}

export const getStatusColor = (status, connectedStatus) => {
  if (status === ApplicantCallConversionStatus.CONNECTED) {
    return `${callStatusColors[status][connectedStatus].backgroundColor} ${callStatusColors[status][connectedStatus].textColor}`
  }
  return `${callStatusColors[status].backgroundColor} ${callStatusColors[status].textColor}`
}

export const getUrlParams = () => {
  const [defaultStartDate, defaultEndDate] = parseDateRangeFromUrl(
    getSearchParam('dateRange')
  ) ?? [dayjs().subtract(30, 'day').startOf('day').toDate(), new Date()]

  return {
    defaultStartDate,
    defaultEndDate,
    defaultCallDirections: getCommaSeparatedSearchParam('callDirections'),
    defaultRecruiters: getCommaSeparatedSearchParam('recruiters'),
    defaultCallTypes: getCommaSeparatedSearchParam('callTypes'),
    defaultCallStatuses: getCommaSeparatedSearchParam('callStatuses'),
    defaultLeadSources: getCommaSeparatedSearchParam('leadSources'),
    defaultConnectedStatus: getCommaSeparatedSearchParam('connectedStatus'),
    defaultJobListings: getCommaSeparatedSearchParam('jobListings'),
    defaultVoiceAgentAnalysisResults:
      getCommaSeparatedSearchParam('analysisResults'),
    defaultVoiceAgents: getCommaSeparatedSearchParam('voiceAgents'),
    defaultCallTags: getCommaSeparatedSearchParam('callTags').filter(Boolean),
    defaultEmployees: getCommaSeparatedSearchParam('employees'),
    defaultSmsDirections: getCommaSeparatedSearchParam('smsDirections'),
    defaultActorTypes: getCommaSeparatedSearchParam('actorTypes'),
  }
}

/**
 * Event name dispatched on `window` after every `updateUrl` write. The page
 * shell uses this to observe URL-backed filter changes that originate inside
 * a tab — `replaceState` doesn't fire `popstate`, so without an explicit
 * signal the page-level hero strip can't tell when a tab's FiltersBar
 * updated the URL (e.g. user picks a new date range) and would otherwise
 * stay stale until the next Refresh-button click.
 */
export const CALL_ANALYTICS_FILTERS_CHANGED_EVENT =
  'call-analytics:filters-changed'

export const updateUrl = (name, value) => {
  setSearchParam(name, value)
  window.dispatchEvent(new Event(CALL_ANALYTICS_FILTERS_CHANGED_EVENT))
}

export const generateMetricsByDateRange = (
  startDate,
  endDate,
  metrics,
  keys,
  metricParam
) => {
  // Helper function to create a date range between two dates
  function createDateRange(start, end) {
    const dateArray = []
    const currentDate = new Date(start)
    end = new Date(end)

    while (currentDate <= end) {
      dateArray.push(new Date(currentDate).toISOString().split('T')[0]) // Store only the date part
      currentDate.setDate(currentDate.getDate() + 1)
    }

    return dateArray
  }

  const defaultValues = keys.reduce((acc, key) => {
    acc[key] = 0
    return acc
  }, {})

  // Helper function to initialize the default object
  function getDefaultMetricsObject(date) {
    return {
      date: dayjs(date).format('MM/DD'),
      ...defaultValues,
    }
  }

  // Create date range
  const dateRange = createDateRange(startDate, endDate)

  // Create a map to store the metrics by date
  const metricsMap = {}

  // Iterate over the metrics array and populate the metricsMap
  metrics.forEach((metric) => {
    const metricDate = metric.date.split('T')[0] // Only use the date part
    if (!metricsMap[metricDate]) {
      metricsMap[metricDate] = getDefaultMetricsObject(metricDate)
    }
    metricsMap[metricDate][formatSnakeValue(metric[metricParam])] +=
      metric.count
  })

  // Generate the final array with default metrics for missing dates
  return dateRange.map((date) => {
    return metricsMap[date] || getDefaultMetricsObject(date)
  })
}

export const callTypeOptions = [
  { value: 'TWILIO', label: 'Recruiter' },
  { value: 'BLANDAI', label: 'Voice Agent' },
]

export const leadSourceOptions = Object.keys(LinkType).map((key) => ({
  value: LinkType[key],
  label: LinkTypeDisplay[LinkType[key]],
}))

/**
 * `CallConnectedStatus` backs two different columns. `ActivityLog.connectedStatus`
 * takes VERIFIED, INFO_COLLECTED, and VOICEMAIL from VoE voice-agent calls, while
 * `ApplicantCallConversion.connectedStatus` only ever holds a recruitment
 * disposition. Offering the VoE values on a conversion filter would give the
 * user a choice that can only ever return an empty result, so the two lists are
 * kept apart and each filter picks the one matching the table it queries.
 */
const VOE_CONNECTED_STATUSES: CallConnectedStatus[] = [
  CallConnectedStatus.VERIFIED,
  CallConnectedStatus.INFO_COLLECTED,
  CallConnectedStatus.VOICEMAIL,
]

const toConnectedStatusOption = (status: CallConnectedStatus) => ({
  value: status,
  label: formatSnakeValue(status),
})

const selectableConnectedStatuses = Object.values(CallConnectedStatus).filter(
  (status) => status !== CallConnectedStatus.CALLBACK
)

/** For filters that query `ActivityLog`, where VoE agent calls are recorded. */
export const activityLogConnectedStatusOptions =
  selectableConnectedStatuses.map(toConnectedStatusOption)

/** For filters that query `ApplicantCallConversion`. */
export const applicantConversionConnectedStatusOptions =
  selectableConnectedStatuses
    .filter((status) => !VOE_CONNECTED_STATUSES.includes(status))
    .map(toConnectedStatusOption)

export const callDirectionOptions = Object.keys(CallDirection).map((key) => ({
  value: CallDirection[key],
  label: formatSnakeValue(CallDirection[key]),
}))

export const callStatusOptions = Object.keys(CallStatus).map((key) => ({
  value: CallStatus[key],
  label: formatSnakeValue(CallStatus[key]),
}))

export const voiceAgentAnalysisResultOptions = Object.keys(
  VoiceAgentAnalysisResultName
).map((key) => ({
  value: VoiceAgentAnalysisResultName[key],
  label: formatSnakeValue(VoiceAgentAnalysisResultName[key]),
}))

export const voiceAgentAnalysisColors = {
  [VoiceAgentAnalysisResultName.CONNECTION_ISSUES]: '#1C93D7', // brand-500
  [VoiceAgentAnalysisResultName.SENSITIVE_INFORMATION]: '#77BEE7', // brand-300
  [VoiceAgentAnalysisResultName.FRUSTRATION]: '#1676AC', // brand-600
  [VoiceAgentAnalysisResultName.DIFFICULTY_BOOKING]: '#49A9DF', // brand-400
  [VoiceAgentAnalysisResultName.LANGUAGE_BARRIERS]: '#A4D4EF', // brand-200
}

export const getVoiceAgentAnalysisColor = (
  resultName: VoiceAgentAnalysisResultName
): string => {
  return voiceAgentAnalysisColors[resultName] || '#BBDFF3' // Default to brand-100 if result not found
}
